import React, { useState, forwardRef, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  MenuItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import CheckIcon from '../../../../assets/check.svg';
import CustomChip from '../Chip/CustomChip';
import CustomSvgIcon from '../SvgIcon/CustomSvgIcon';

export interface Option {
  name: string;
  id: string | number;
  code?: string;
}

interface AutocompleteMultiselectProps {
  id: string;
  label?: string;
  options: Option[];
  placeholder?: string;
  limitTags?: number;
  size?: 'small' | 'medium';
  helperText?: string;
  autoComplete?: boolean;
  disableClearable?: boolean;
  openOnFocus?: boolean;
  disableCloseOnSelect?: boolean;
  groupBy?: (option: Option) => string;
  onChange?: (selectedOptions: Option[]) => void;
  loading?: boolean;
  noOptionsText?: string;
  clearOnBlur?: boolean;
  disabled?: boolean;
  sortOrder?: 'asc' | 'desc';
  onDelete?: (deletedValue: Option, updatedValues?: Option[]) => void;
  value?: Option[];
}

const AutocompleteMultiselect = forwardRef<
  HTMLDivElement,
  AutocompleteMultiselectProps
>(
  (
    {
      id,
      label,
      options,
      placeholder = 'Search...',
      limitTags,
      size = 'medium',
      helperText,
      autoComplete,
      disableClearable = false,
      openOnFocus = true,
      disableCloseOnSelect = false,
      groupBy,
      loading = false,
      noOptionsText = 'No options',
      clearOnBlur = true,
      disabled = false,
      sortOrder,
      onChange,
      value,
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] = useState<Option[]>([]);

    useEffect(() => {
      setSelectedValues(value || []);
    }, [value]);

    const sortedOptions = sortOrder
      ? [...options].sort((a, b) => {
          if (a.name < b.name) return sortOrder === 'asc' ? -1 : 1;
          if (a.name > b.name) return sortOrder === 'asc' ? 1 : -1;
          return 0;
        })
      : options;

    const handleChange = (
      _event: React.SyntheticEvent,
      newValue: (string | Option)[]
    ) => {
      const filteredNewValues = newValue.filter(
        (item): item is Option => typeof item !== 'string'
      );

      const uniqueValues = filteredNewValues.filter(
        (item, index, self) => index === self.findIndex((t) => t.id === item.id)
      );

      onChange?.(uniqueValues); // ✅ only call parent
    };

    const handleDelete = (valueToDelete: Option) => {
      const updatedValues =
        value && value.filter((item) => item.id !== valueToDelete.id);

      onChange?.(updatedValues ?? []); // ✅ update parent only
    };

    return (
      <Autocomplete
        ref={ref}
        id={id}
        multiple
        options={sortedOptions}
        autoComplete={autoComplete}
        limitTags={limitTags}
        size={size}
        value={selectedValues}
        onChange={handleChange}
        disableClearable={disableClearable}
        openOnFocus={openOnFocus}
        disableCloseOnSelect={disableCloseOnSelect}
        groupBy={groupBy}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={loading}
        noOptionsText={noOptionsText}
        clearOnBlur={clearOnBlur}
        disabled={disabled}
        getOptionLabel={(option) => option.name}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => {
            /* eslint-disable @typescript-eslint/no-unused-vars */
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <CustomChip
                id="autocomplete-chip"
                key={option.id}
                size={size}
                label={option.name}
                {...tagProps}
                onDelete={() => handleDelete(option)}
              />
            );
          })
        }
        renderOption={(props, option) => (
          <MenuItem
            {...props}
            key={option.id}
            value={option.id}
            sx={{
              backgroundColor: selectedValues.some(
                (value) => value.id === option.id
              )
                ? 'rgba(0, 123, 255, 0.1)'
                : 'inherit',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            {option.code && (
              <img
                loading="lazy"
                width="20"
                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                alt=""
              />
            )}
            <ListItemText
              primaryTypographyProps={{ fontSize: '12px' }}
              primary={option.name}
            />
            {selectedValues.some((value) => value.id === option.id) && (
              <CustomSvgIcon fontSize="inherit">
                <CheckIcon />
              </CustomSvgIcon>
            )}
          </MenuItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            helperText={helperText}
            size={size}
            disabled={disabled}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        slotProps={{
          clearIndicator: {
            sx: {
              '& svg': {
                fontSize: 16, // <-- adjust size here
              },
            },
          },
        }}
      />
    );
  }
);

AutocompleteMultiselect.displayName = 'AutocompleteMultiselect';

export default AutocompleteMultiselect;
